import React from 'react'
import linkIconSrc from '../../../../../../images/common/linkIcon.png'
import { useLocation } from 'react-router-dom'
import { apiCall } from 'utils/restclient'
import { windowOpenArgs } from 'utils/windowOpenArgs'
import spinnerIcon from '../../../../../../images/common/spinner.svg'
import './PdmpInfo.scss'
import { useSelector } from 'store/hooks/useSelector'

const pdmpTooltipId = 'pdmp-tooltip'

function Tooltip({ children }: React.PropsWithChildren) {
  return (
    <div
      id={pdmpTooltipId}
      role='tooltip'
      style={{
        transform: 'translate(-50%, -100%)',
        whiteSpace: 'nowrap',
        paddingLeft: 16,
        paddingRight: 16,
        paddingTop: 8,
        paddingBottom: 8,
        left: '50%',
        top: -5,
        position: 'absolute',
        background: 'var(--Colors-Neutral-Grey-Blue, #C2D5E2)',
        borderRadius: 4,
        color: 'var(--Text-Colors-Dark, #111)',
        fontSize: '18px',
        fontWeight: 400,
        letterSpacing: 1.2,
      }}>
      {children}
    </div>
  )
}

const formatUtcTimestamp = (utcTimestamp: string) => {
  const date = new Date(utcTimestamp)

  const day = String(date.getDate()).padStart(2, '0')
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const year = date.getFullYear()

  const formatted = `${month}/${day}/${year}`

  return formatted
}

type PdmpState = {
  // little weird but we want to show an error state even if there isn't a message, so we use the object even though the
  // only property is the message since the message might be an empty string
  error: null | { message: string }
  loading: boolean
}

type PdmpResponse = {
  Error: string | null
  ReportUrl: string | null
}

const usePdmpInfo = () => {
  const [pdmpState, setPdmpState] = React.useState<PdmpState>({ error: null, loading: false })

  const { search } = useLocation()
  const patientId = React.useMemo(() => new URLSearchParams(search).get('id'), [search])

  // @ts-expect-error TS2538 -- MIGRATION: not approved, fix when encountered
  const patientInfo = useSelector((state) => state.chartsReducer.patientInfo?.[patientId])

  const lastPdmpViewUtcTimestamp = patientInfo?.PrescriptionMonitoringProgram?.LastSeenDateTimeUtc
  const lastPdmpViewing = lastPdmpViewUtcTimestamp ? formatUtcTimestamp(lastPdmpViewUtcTimestamp) : null

  const stateCode = patientInfo?.Address?.StateCode?.toUpperCase()
  const getPdmpUrl = React.useCallback(async () => {
    setPdmpState({ loading: true, error: null })

    const nextState: PdmpState = { loading: false, error: null }
    try {
      // @ts-expect-error TS2322 -- MIGRATION: not approved, fix when encountered
      const response: PdmpResponse = await apiCall(
        `api/v1/patients/${patientId}/prescription-monitoring-program`,
        'GET',
      )
      if (response.ReportUrl) {
        window.open(response.ReportUrl, ...windowOpenArgs)
      } else {
        nextState.error = { message: response.Error || '' }
      }
    } catch (error) {
      // @ts-expect-error TS18046 -- MIGRATION: not approved, fix when encountered
      nextState.error = error.message
    } finally {
      nextState.loading = false
    }

    setPdmpState(nextState)
  }, [patientId])

  return { lastPdmpViewing, stateCode, getPdmpUrl, ...pdmpState }
}

export function PdmpInfo() {
  const { lastPdmpViewing, stateCode, getPdmpUrl, error, loading } = usePdmpInfo()

  const [showTooltip, setShowTooltip] = React.useState(false)

  const handleMouseEnter = () => {
    setShowTooltip(true)
  }

  const handleMouseLeave = () => {
    setShowTooltip(false)
  }

  const stateNotIntegrated = useSelector(
    (state) => !state?.flags?.flags?.pdmp_integrated_states?.includes(stateCode || ''),
  )

  const isInInactiveState = loading || stateNotIntegrated

  const tooltipContent =
    error ? error.message
    : lastPdmpViewing ? `Last viewed: ${lastPdmpViewing}`
    : null

  return (
    <div
      role='button'
      tabIndex={0}
      aria-describedby={pdmpTooltipId}
      style={{
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        minWidth: 140,
      }}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onFocus={handleMouseEnter}
      onBlur={handleMouseLeave}>
      {showTooltip && tooltipContent ? <Tooltip>{tooltipContent}</Tooltip> : null}
      <span className='patient-info-label'>PDMP:</span>
      {isInInactiveState ?
        <span className='patient-info-label' style={{ display: 'flex', alignItems: 'center', marginRight: 0 }}>
          {loading ?
            <img role='presentation' src={spinnerIcon} alt='loading' className='spin' />
          : `${stateCode ? `${stateCode} ` : ''}Not integrated`}
        </span>
      : <button
          type='button'
          style={{ all: 'unset', cursor: 'pointer', display: 'contents' }}
          disabled={loading}
          onClick={getPdmpUrl}>
          {error ?
            <span className='patient-info-label' style={{ display: 'flex', alignItems: 'center', marginRight: 0 }}>
              Error
            </span>
          : <>
              <span className='patient-info-value'>View</span>
              <img src={linkIconSrc} alt='pdmp-external-link' style={{ width: 24, height: 24, marginLeft: 8 }} />
            </>
          }
        </button>
      }
    </div>
  )
}
